import { useState, useEffect, useCallback } from "react";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import { PostWindowEventName, ProcedureType, Session } from "api/type";
import { getUserLocation, TGetUserLocationResponse } from "api/clinicos";

export const usePoseLandmarker = () => {
  const [poseLandmarker, setPoseLandmarker] = useState<
    PoseLandmarker | "ERROR" | null
  >(null);

  useEffect(() => {
    const createPoseLandmarker = async () => {
      const taskVisionVersion =
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm";
      const taskVisionModel =
        "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task";

      try {
        const vision = await FilesetResolver.forVisionTasks(taskVisionVersion);
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: taskVisionModel,
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          minPoseDetectionConfidence: 0.75,
          minPosePresenceConfidence: 0.75,
          minTrackingConfidence: 0.75,
        });
        setPoseLandmarker(landmarker);
      } catch (error) {
        console.error("Error creating pose landmarker:", error);
        setPoseLandmarker("ERROR");
      }
    };

    createPoseLandmarker();
  }, []);

  return poseLandmarker;
};

export const useContextProcedure = () => {
  const [contextProcedure, setContextProcedure] =
    useState<ProcedureType | null>(null);

  useEffect(() => {
    const handleMessage = (event: { data: any }) => {
      const messageData = event.data;
      if (
        messageData.message === PostWindowEventName.CONTEXT_PROCEDURE_UPDATE
      ) {
        setContextProcedure(
          messageData.payload.contextProcedure as ProcedureType,
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return contextProcedure;
};

export const useUserLocation = (session: Session | null) => {
  const fetchUserLocation = useCallback(async () => {
    if (!session) return;

    try {
      const response: TGetUserLocationResponse = await getUserLocation(session);
      const location = `${response.city}, ${response.subdivision}`;
      return location;
    } catch (error) {
      console.error("Error fetching user location:", error);
      return "Unknown";
    }
  }, [session]);

  return fetchUserLocation;
};

const fontMapper: Record<string, string> = {
  Montserrat:
    "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap",
};
export const useDynamicFont = () => {
  const [fontFamily, setFontFamily] = useState<string>();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queriedFont = urlParams.get("fontFamily") || "Urbanist";
    setFontFamily(queriedFont);

    const link = document.createElement("link");
    const fontLink = fontMapper[queriedFont || "Urbanist"];

    link.href = fontLink;
    link.rel = "stylesheet";
    document.head.appendChild(link);

    document.documentElement.style.setProperty(
      "--dynamic-font-family",
      queriedFont,
    );

    return () => {
      document.head.removeChild(link);
    };
  }, []);
  return fontFamily;
};
